import { useCallback, useEffect, useMemo, useState, Fragment } from "react";
import { ChevronLeft, ChevronRight, FileText, CalendarRange, Check, X, Building2, AlertTriangle, Settings2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtFecha, MESES, MESES_CORTOS, hoyISO } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PageHeader from "@/components/PageHeader";
import PanelConfigDialog, { GRUPOS_PLANIFICACION } from "@/components/PanelConfigDialog";

function sumarDiasISO(iso, dias) {
  const d = new Date(iso + "T12:00:00");
  d.setDate(d.getDate() + dias);
  return d.toLocaleDateString("sv");
}

const CELDA = {
  L: { className: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200", label: "Previsto (sin fichar)" },
  P: { className: "bg-green-600 text-white", label: "Presente" },
  T: { className: "bg-green-100 text-green-700", label: "Trabajado" },
  R: { className: "bg-yellow-100 text-yellow-800 ring-1 ring-yellow-300", label: "Retraso (incidencia)" },
  V: { className: "bg-amber-100 text-amber-700", label: "Vacaciones" },
  A: { className: "bg-orange-100 text-orange-700", label: "Ausencia" },
  B: { className: "bg-red-100 text-red-700", label: "Baja" },
};

const DIAS_SEMANA = ["L", "M", "X", "J", "V", "S", "D"];

const NIVEL_DISPONIBILIDAD = {
  normal: { className: "bg-emerald-200 ring-emerald-300", label: "Normal" },
  bajo: { className: "bg-amber-300 ring-amber-400", label: "Baja cobertura" },
  critico: { className: "bg-red-400 ring-red-500", label: "Crítico" },
};

function LeyendaEstados() {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-2 rounded-xl border border-teal-100 bg-gradient-to-r from-teal-50/80 to-cyan-50/80 px-4 py-3 text-xs text-zinc-600">
      {Object.entries(CELDA).map(([k, v]) => (
        <span key={k} className="flex items-center gap-1.5">
          <span className={`inline-flex h-4 w-4 items-center justify-center rounded text-[9px] font-semibold ${v.className}`}>
            {k}
          </span>
          {v.label}
        </span>
      ))}
      <span className="flex items-center gap-1.5">
        <span className="inline-block h-3 w-3 rounded bg-zinc-100 ring-1 ring-zinc-200" />
        Descanso / fin de semana
      </span>
      <span className="flex items-center gap-1.5">
        <span className="inline-block h-3 w-3 rounded bg-sky-100 ring-1 ring-sky-200" />
        Festivo
      </span>
      <span className="flex items-center gap-1.5 text-zinc-500">
        V (hábiles): solo días laborables del trabajador
      </span>
    </div>
  );
}

const MOTIVO_AUSENCIA = {
  V: "Vacaciones",
  B: "Baja",
  A: "Ausencia",
  "Vacaciones pendientes": "Vac. pendiente",
};

function LineaVacacionesAnual({ vacaciones, lineaMeses, year }) {
  const { disponibles, usados, pendientes, sin_asignar } = vacaciones;
  const total = disponibles || 1;
  const pct = (n) => Math.max(0, Math.min(100, (n / total) * 100));

  return (
    <div className="space-y-1.5">
      <div
        className="flex h-3 w-full overflow-hidden rounded-full bg-zinc-100 ring-1 ring-zinc-200/80"
        title={`${usados} d asignados · ${pendientes} d pendientes · ${sin_asignar} d sin asignar`}
      >
        {usados > 0 && (
          <div className="bg-emerald-500 transition-all" style={{ width: `${pct(usados)}%` }} />
        )}
        {pendientes > 0 && (
          <div className="bg-amber-400 transition-all" style={{ width: `${pct(pendientes)}%` }} />
        )}
        {sin_asignar > 0 && (
          <div className="bg-zinc-200 transition-all" style={{ width: `${pct(sin_asignar)}%` }} />
        )}
      </div>
      {lineaMeses?.length === 12 && (
        <div className="flex gap-px">
          {lineaMeses.map((m) => {
            const tiene = m.aprobados + m.pendientes > 0;
            const mix = m.aprobados + m.pendientes;
            const intensidad = mix >= 5 ? "bg-amber-500" : mix >= 3 ? "bg-amber-400" : mix >= 1 ? "bg-amber-300" : "bg-zinc-100";
            return (
              <div
                key={m.mes}
                title={`${MESES_CORTOS[m.mes - 1]} ${year}: ${m.aprobados} aprob. · ${m.pendientes} pend.`}
                className={`h-1.5 flex-1 rounded-sm ${tiene ? intensidad : "bg-zinc-100"}`}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function FilaEmpleadoCalendario({ emp, datos, year, month, diaHoy }) {
  return (
    <tr className="hover:bg-zinc-50">
      <td className="sticky left-0 z-10 border-b border-r border-zinc-100 bg-white px-4 py-2">
        <p className="font-medium leading-tight">{emp.nombre}</p>
        <p className="text-[11px] text-zinc-400">{emp.departamento_nombre || emp.departamento || "—"}</p>
      </td>
      {Array.from({ length: datos.num_dias }, (_, i) => i + 1).map((d) => {
        const codigo = emp.dias[d] || emp.dias[String(d)];
        const finde = datos.fines_semana.includes(d);
        const festivo = datos.festivos?.some((f) => f.dia === d);
        const celda = codigo ? CELDA[codigo] : null;
        const esHoy = d === diaHoy;
        return (
          <td
            key={d}
            className={`border-b border-zinc-100 p-0.5 text-center ${festivo ? "bg-sky-50" : finde ? "bg-zinc-50/60" : ""} ${esHoy ? "ring-1 ring-inset ring-zinc-900/20" : ""}`}
          >
            {celda ? (
              <span
                className={`mx-auto flex h-6 w-6 items-center justify-center rounded text-[10px] font-semibold ${celda.className}`}
                title={celda.label}
              >
                {codigo}
              </span>
            ) : null}
          </td>
        );
      })}
    </tr>
  );
}

function TablaCalendario({ datos, year, month, diaHoy }) {
  const grupos = datos.grupos?.length ? datos.grupos : [{ centro_nombre: null, departamentos: [{ departamento_nombre: null, empleados: datos.empleados }] }];

  return (
    <table className="w-full border-collapse text-sm" data-testid="planificacion-tabla">
      <thead>
        <tr>
          <th className="sticky left-0 z-10 min-w-44 border-b border-r border-zinc-200 bg-white px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.1em] text-zinc-500">
            Empleado
          </th>
          {Array.from({ length: datos.num_dias }, (_, i) => i + 1).map((d) => {
            const finde = datos.fines_semana.includes(d);
            const festivo = datos.festivos?.find((f) => f.dia === d);
            const esHoy = d === diaHoy;
            return (
              <th
                key={d}
                title={festivo?.nombre}
                className={`border-b border-zinc-200 px-0.5 py-2 text-center text-[10px] font-medium ${
                  festivo ? "bg-sky-100 text-sky-600" : finde ? "bg-zinc-50 text-zinc-300" : "text-zinc-500"
                } ${esHoy ? "bg-zinc-950 text-white" : ""}`}
              >
                <div>{DIAS_SEMANA[new Date(year, month - 1, d).getDay() === 0 ? 6 : new Date(year, month - 1, d).getDay() - 1]}</div>
                <div className="font-semibold">{d}</div>
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {grupos.map((centro) =>
          centro.departamentos.map((dep) => (
            <Fragment key={`g-${centro.centro_id}-${dep.departamento_id}`}>
              <tr className="bg-zinc-50/80">
                <td
                  colSpan={datos.num_dias + 1}
                  className="border-b border-zinc-200 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-600"
                >
                  <span className="inline-flex items-center gap-1.5">
                    <Building2 className="h-3 w-3 text-teal-600" strokeWidth={1.5} />
                    {centro.centro_nombre}
                    <span className="font-normal text-zinc-400">·</span>
                    {dep.departamento_nombre}
                  </span>
                </td>
              </tr>
              {dep.empleados.map((emp) => (
                <FilaEmpleadoCalendario key={emp.id} emp={emp} datos={datos} year={year} month={month} diaHoy={diaHoy} />
              ))}
            </Fragment>
          ))
        )}
        {datos.empleados.length === 0 && (
          <tr>
            <td colSpan={datos.num_dias + 1} className="p-6 text-center text-zinc-400">
              No hay empleados.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

function PanelVacaciones({ vacData, empleadoSel }) {
  if (!vacData) return null;
  const emp = empleadoSel
    ? vacData.grupos.flatMap((c) => c.departamentos.flatMap((d) => d.empleados)).find((e) => e.id === empleadoSel)
    : null;

  return (
    <div className="rounded-2xl border border-teal-100 bg-gradient-to-r from-teal-50/60 via-white to-cyan-50/40 p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-teal-800">Vacaciones {vacData.year}</p>
          <p className="text-[11px] text-zinc-500">Resumen del ámbito visible</p>
        </div>
        <div className="grid flex-1 grid-cols-2 gap-2 sm:grid-cols-3 lg:max-w-xl">
          <div className="rounded-xl border border-emerald-100 bg-emerald-50/80 px-3 py-2">
            <p className="text-[10px] font-medium uppercase text-emerald-700">Asignadas</p>
            <p className="text-xl font-semibold text-emerald-900">{vacData.totales.usados} d</p>
          </div>
          <div className="rounded-xl border border-amber-100 bg-amber-50/80 px-3 py-2">
            <p className="text-[10px] font-medium uppercase text-amber-700">Pendientes</p>
            <p className="text-xl font-semibold text-amber-900">{vacData.totales.pendientes} d</p>
          </div>
          <div className="col-span-2 rounded-xl border border-zinc-200 bg-zinc-50/80 px-3 py-2 sm:col-span-1">
            <p className="text-[10px] font-medium uppercase text-zinc-500">Sin asignar</p>
            <p className="text-xl font-semibold text-zinc-800">{vacData.totales.sin_asignar} d</p>
            <p className="text-[10px] text-zinc-400">de {vacData.totales.disponibles} d</p>
          </div>
        </div>
        {emp && (
          <div className="w-full rounded-xl border border-zinc-200 bg-white p-3 lg:max-w-xs">
            <p className="text-xs font-semibold text-zinc-700">{emp.nombre}</p>
            <p className="text-[10px] text-zinc-400">
              {emp.centro_nombre} · {emp.departamento_nombre}
            </p>
            <div className="mt-2 grid grid-cols-3 gap-1 text-center text-[10px]">
              <div>
                <p className="font-semibold text-emerald-700">{emp.vacaciones.usados}</p>
                <p className="text-zinc-400">Asignadas</p>
              </div>
              <div>
                <p className="font-semibold text-amber-700">{emp.vacaciones.pendientes}</p>
                <p className="text-zinc-400">Pendientes</p>
              </div>
              <div>
                <p className="font-semibold text-zinc-700">{emp.vacaciones.sin_asignar}</p>
                <p className="text-zinc-400">Sin asignar</p>
              </div>
            </div>
          </div>
        )}
      </div>
      {!emp && (
        <p className="mt-3 text-center text-[11px] text-zinc-400">
          Selecciona un trabajador en la sección inferior para ver su detalle.
        </p>
      )}
    </div>
  );
}

function SeccionDisponibilidad({ desde, hasta, onDesde, onHasta, disponibilidad, cargando }) {
  const hoy = hoyISO();

  const aplicarRango = (dias) => {
    onDesde(hoy);
    onHasta(sumarDiasISO(hoy, dias));
  };

  const fmtDiaCorto = (iso) => {
    const d = new Date(iso + "T12:00:00");
    return d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit" });
  };

  const fmtPeriodo = (p) => {
    const ini = p.fecha_desde || p.desde;
    const fin = p.fecha_hasta || p.hasta;
    if (typeof ini === "string" && ini.includes("-")) {
      return ini === fin ? fmtFecha(ini) : `${fmtFecha(ini)} — ${fmtFecha(fin)}`;
    }
    return fin !== ini ? `Días ${ini}–${fin}` : `Día ${ini}`;
  };

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white shadow-sm">
      <div className="border-b border-zinc-100 px-4 py-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600" strokeWidth={1.5} />
              <p className="font-display text-sm font-semibold text-zinc-900">Disponibilidad y días críticos</p>
            </div>
            <p className="mt-1 text-xs text-zinc-500">
              Proyección futura por centro y departamento (vacaciones, bajas, ausencias y solicitudes pendientes)
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <Label className="text-[10px] text-zinc-500">Desde</Label>
              <Input
                type="date"
                value={desde}
                min={hoy}
                onChange={(e) => onDesde(e.target.value < hoy ? hoy : e.target.value)}
                className="h-8 w-36 text-xs"
              />
            </div>
            <div>
              <Label className="text-[10px] text-zinc-500">Hasta</Label>
              <Input
                type="date"
                value={hasta}
                min={desde}
                onChange={(e) => onHasta(e.target.value)}
                className="h-8 w-36 text-xs"
              />
            </div>
            <div className="flex gap-1 pb-0.5">
              {[7, 30, 90].map((d) => (
                <Button key={d} type="button" variant="outline" size="sm" className="h-8 text-[10px]" onClick={() => aplicarRango(d)}>
                  {d} d
                </Button>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-2 flex flex-wrap gap-3 text-[10px] text-zinc-500">
          {Object.entries(NIVEL_DISPONIBILIDAD).map(([k, v]) => (
            <span key={k} className="flex items-center gap-1">
              <span className={`inline-block h-2.5 w-4 rounded-sm ring-1 ${v.className}`} />
              {v.label}
            </span>
          ))}
          <span className="flex items-center gap-1">
            <span className="inline-block h-2.5 w-4 rounded-sm bg-zinc-100 ring-1 ring-zinc-200" />
            Sin jornada
          </span>
        </div>
      </div>

      {cargando && <p className="p-6 text-center text-sm text-zinc-400">Calculando disponibilidad…</p>}

      {!cargando && disponibilidad?.grupos?.length === 0 && (
        <p className="p-6 text-center text-sm text-zinc-400">No hay empleados en el ámbito.</p>
      )}

      {!cargando && disponibilidad?.grupos?.length > 0 && (
        <div className="divide-y divide-zinc-100">
          {disponibilidad.grupos.map((centro) => (
            <div key={centro.centro_id ?? centro.centro_nombre}>
              <div className="bg-zinc-50 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-zinc-600">
                <Building2 className="mr-1.5 inline h-3 w-3 text-teal-600" strokeWidth={1.5} />
                {centro.centro_nombre}
              </div>
              {centro.departamentos.map((dep) => (
                <div key={dep.departamento_id ?? dep.departamento_nombre} className="border-t border-zinc-50 px-4 py-4">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium text-zinc-800">{dep.departamento_nombre}</p>
                      <p className="text-[11px] text-zinc-400">{dep.total_empleados} trabajadores</p>
                    </div>
                    {dep.dias_criticos?.length > 0 && (
                      <Badge variant="outline" className="border-red-200 bg-red-50 text-[10px] text-red-700">
                        {dep.dias_criticos.length} {dep.dias_criticos.length === 1 ? "día crítico" : "días críticos"}
                      </Badge>
                    )}
                  </div>

                  <div className="overflow-x-auto">
                    <div className="min-w-max">
                      <div className="mb-1 flex gap-px">
                        {dep.dias.map((d) => {
                          const esHoy = d.fecha === hoy;
                          const finde = d.dia_semana >= 5;
                          return (
                            <div
                              key={d.fecha}
                              className={`w-5 text-center text-[8px] ${finde ? "text-zinc-300" : "text-zinc-400"} ${esHoy ? "font-bold text-zinc-900" : ""}`}
                            >
                              {fmtDiaCorto(d.fecha)}
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex gap-px">
                        {dep.dias.map((d) => {
                          const cfg = NIVEL_DISPONIBILIDAD[d.nivel];
                          const esHoy = d.fecha === hoy;
                          const tooltip = d.laborable
                            ? `${fmtFecha(d.fecha)}: ${d.disponibles}/${d.total} disponibles (${d.pct}%)`
                            : `${fmtFecha(d.fecha)}: sin jornada laborable`;
                          const detalle = d.detalle_ausentes?.length
                            ? `\nAusentes: ${d.detalle_ausentes.map((a) => `${a.nombre} (${MOTIVO_AUSENCIA[a.motivo] || a.motivo})`).join(", ")}`
                            : "";
                          return (
                            <div
                              key={d.fecha}
                              title={tooltip + detalle}
                              className={`h-5 w-5 rounded-sm ring-1 ${
                                d.laborable ? cfg?.className || "bg-zinc-200" : "bg-zinc-100 ring-zinc-200"
                              } ${esHoy ? "ring-2 ring-zinc-900" : ""}`}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {dep.periodos?.length > 0 && (
                    <div className="mt-3 space-y-1.5">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">Periodos con baja cobertura</p>
                      {dep.periodos.map((p, i) => (
                        <div
                          key={`${p.fecha_desde || p.desde}-${p.fecha_hasta || p.hasta}-${i}`}
                          className={`flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-xs ${
                            p.nivel === "critico" ? "border-red-200 bg-red-50/80 text-red-900" : "border-amber-200 bg-amber-50/80 text-amber-950"
                          }`}
                        >
                          <span className="font-semibold">{fmtPeriodo(p)}</span>
                          <span className="text-zinc-500">·</span>
                          <span>
                            Mín. {p.min_disponibles} de {p.total} disponible{p.min_disponibles !== 1 ? "s" : ""}
                          </span>
                          <Badge variant="outline" className={`text-[9px] ${p.nivel === "critico" ? "border-red-300 text-red-700" : "border-amber-300 text-amber-800"}`}>
                            {NIVEL_DISPONIBILIDAD[p.nivel]?.label || p.nivel}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}

                  {dep.periodos?.length === 0 && dep.dias_criticos?.length === 0 && (
                    <p className="mt-2 text-[11px] text-emerald-600">Cobertura adecuada en el periodo seleccionado.</p>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ListadoTrabajadoresVacaciones({ vacData, year, empleadoSel, onSelectEmpleado }) {
  if (!vacData) return null;

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white shadow-sm">
      <div className="border-b border-zinc-100 px-4 py-3">
        <p className="font-display text-sm font-semibold text-zinc-900">Trabajadores — vacaciones {year}</p>
        <p className="text-xs text-zinc-500">Una línea por trabajador: asignadas · pendientes · sin asignar</p>
        <div className="mt-2 flex flex-wrap gap-3 text-[10px] text-zinc-500">
          <span className="flex items-center gap-1">
            <span className="inline-block h-2 w-4 rounded-full bg-emerald-500" /> Asignadas (aprobadas)
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block h-2 w-4 rounded-full bg-amber-400" /> Pendientes de aprobar
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block h-2 w-4 rounded-full bg-zinc-200" /> Sin asignar
          </span>
        </div>
      </div>

      <div className="divide-y divide-zinc-100">
        {vacData.grupos.map((centro) => (
          <div key={centro.centro_id ?? centro.centro_nombre}>
            <div className="bg-zinc-50 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-zinc-600">
              <Building2 className="mr-1.5 inline h-3 w-3 text-teal-600" strokeWidth={1.5} />
              {centro.centro_nombre}
            </div>
            {centro.departamentos.map((dep) => (
              <div key={dep.departamento_id ?? dep.departamento_nombre}>
                <div className="border-t border-zinc-100 bg-zinc-50/50 px-4 py-1.5 pl-8 text-[10px] font-medium text-zinc-500">
                  {dep.departamento_nombre}
                </div>
                {dep.empleados.map((emp) => (
                  <button
                    key={emp.id}
                    type="button"
                    onClick={() => onSelectEmpleado(emp.id === empleadoSel ? null : emp.id)}
                    className={`flex w-full flex-wrap items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-teal-50/40 ${
                      empleadoSel === emp.id ? "bg-teal-50 ring-1 ring-inset ring-teal-200" : ""
                    }`}
                  >
                    <div className="min-w-[9rem] shrink-0">
                      <p className="text-sm font-medium text-zinc-900">{emp.nombre}</p>
                      <p className="text-[10px] text-zinc-400">{emp.vacaciones.modo_label}</p>
                    </div>
                    <div className="min-w-0 flex-1">
                      <LineaVacacionesAnual vacaciones={emp.vacaciones} lineaMeses={emp.linea_meses} year={year} />
                    </div>
                    <div className="flex shrink-0 gap-3 text-right text-[11px] tabular-nums">
                      <div>
                        <p className="font-semibold text-emerald-700">{emp.vacaciones.usados}</p>
                        <p className="text-zinc-400">asig.</p>
                      </div>
                      <div>
                        <p className="font-semibold text-amber-700">{emp.vacaciones.pendientes}</p>
                        <p className="text-zinc-400">pend.</p>
                      </div>
                      <div>
                        <p className="font-semibold text-zinc-700">{emp.vacaciones.sin_asignar}</p>
                        <p className="text-zinc-400">libres</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ))}
          </div>
        ))}
        {vacData.grupos.length === 0 && (
          <p className="p-6 text-center text-sm text-zinc-400">No hay empleados en el ámbito.</p>
        )}
      </div>
    </div>
  );
}

function SolicitudesPendientes({ vacData, onAprobar, onRechazar, aprobando }) {
  if (!vacData?.solicitudes_pendientes?.length) return null;

  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50/80 p-4">
      <p className="mb-3 text-sm font-semibold text-amber-950">Solicitudes pendientes de aprobación</p>
      <div className="space-y-2">
        {vacData.solicitudes_pendientes.map((p) => (
          <div
            key={p.id}
            className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-100 bg-white px-4 py-3 text-sm"
          >
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-medium text-zinc-900">{p.nombre}</p>
                {p.tipo === "vacaciones_ajuste" && (
                  <Badge variant="outline" className="border-violet-300 text-[10px] text-violet-700">
                    Ajuste
                  </Badge>
                )}
              </div>
              <p className="text-xs text-zinc-500">
                {p.centro_nombre} · {p.departamento_nombre}
              </p>
              <p className="mt-0.5 text-xs font-medium text-violet-800">
                {fmtFecha(p.fecha_inicio)} — {fmtFecha(p.fecha_fin)}
              </p>
              {p.motivo && <p className="mt-1 text-xs text-zinc-500">{p.motivo}</p>}
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="border-red-200 text-red-700 hover:bg-red-50"
                disabled={aprobando === p.id}
                onClick={() => onRechazar(p)}
              >
                <X className="mr-1 h-3.5 w-3.5" />
                Rechazar
              </Button>
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700"
                disabled={aprobando === p.id}
                onClick={() => onAprobar(p)}
              >
                <Check className="mr-1 h-3.5 w-3.5" />
                {aprobando === p.id ? "Procesando…" : "Aprobar"}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Planificacion() {
  const hoy = new Date();
  const [year, setYear] = useState(hoy.getFullYear());
  const [month, setMonth] = useState(hoy.getMonth() + 1);
  const [datos, setDatos] = useState(null);
  const [vacData, setVacData] = useState(null);
  const [dispData, setDispData] = useState(null);
  const [dispDesde, setDispDesde] = useState(hoyISO());
  const [dispHasta, setDispHasta] = useState(sumarDiasISO(hoyISO(), 30));
  const [dispCargando, setDispCargando] = useState(false);
  const [empleadoSel, setEmpleadoSel] = useState(null);
  const [aprobando, setAprobando] = useState(null);
  const [planConfig, setPlanConfig] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);

  const visibleSet = useMemo(() => new Set(planConfig?.visible || []), [planConfig]);
  const orden = planConfig?.orden || [];

  const necesitaCalendario = useMemo(
    () => !planConfig || visibleSet.has("calendario_mensual"),
    [planConfig, visibleSet]
  );
  const necesitaVac = useMemo(
    () => planConfig && ["resumen_vacaciones", "listado_trabajadores", "solicitudes_pendientes"].some((id) => visibleSet.has(id)),
    [planConfig, visibleSet]
  );
  const necesitaDisp = useMemo(
    () => planConfig && visibleSet.has("disponibilidad_critica"),
    [planConfig, visibleSet]
  );

  const cargarConfig = useCallback(async () => {
    try {
      const { data } = await api.get("/planificacion/config");
      setPlanConfig(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  const cargar = useCallback(async () => {
    if (!planConfig) return;
    try {
      const reqs = [];
      if (necesitaCalendario) {
        reqs.push(api.get("/planificacion", { params: { year, month } }));
      }
      if (necesitaVac) {
        reqs.push(api.get("/planificacion/vacaciones-anual", { params: { year } }));
      }
      const results = await Promise.all(reqs);
      let i = 0;
      if (necesitaCalendario) {
        setDatos(results[i].data);
        i += 1;
      } else {
        setDatos(null);
      }
      if (necesitaVac) {
        setVacData(results[i].data);
      } else {
        setVacData(null);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [year, month, planConfig, necesitaCalendario, necesitaVac]);

  const cargarDisponibilidad = useCallback(async () => {
    if (!necesitaDisp || !dispDesde || !dispHasta || dispDesde > dispHasta) return;
    setDispCargando(true);
    try {
      const { data } = await api.get("/planificacion/disponibilidad", {
        params: { desde: dispDesde, hasta: dispHasta },
      });
      setDispData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDispCargando(false);
    }
  }, [dispDesde, dispHasta, necesitaDisp]);

  useEffect(() => {
    cargarConfig();
  }, [cargarConfig]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  useEffect(() => {
    cargarDisponibilidad();
  }, [cargarDisponibilidad]);

  const navegar = (delta) => {
    let m = month + delta;
    let y = year;
    if (m < 1) {
      m = 12;
      y -= 1;
    }
    if (m > 12) {
      m = 1;
      y += 1;
    }
    setMonth(m);
    setYear(y);
  };

  const exportarPdf = async () => {
    try {
      const res = await api.get("/planificacion/export-pdf", { params: { year, month }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `planificacion_${year}_${String(month).padStart(2, "0")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Planificación descargada");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const revisarSolicitud = async (p, accion) => {
    setAprobando(p.id);
    try {
      await api.put(`/solicitudes/${p.id}/revisar`, {
        accion,
        comentario: accion === "aprobar" ? "Revisada desde planificación" : "Rechazada desde planificación",
      });
      toast.success(accion === "aprobar" ? "Vacaciones aprobadas" : "Solicitud rechazada");
      cargar();
      cargarDisponibilidad();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAprobando(null);
    }
  };

  const diaHoy =
    datos && datos.hoy.startsWith(`${year}-${String(month).padStart(2, "0")}`)
      ? Number(datos.hoy.slice(8, 10))
      : null;

  const seccionesVisibles = orden.filter((id) => visibleSet.has(id));

  const renderSeccion = (id) => {
    switch (id) {
      case "leyenda_estados":
        return <LeyendaEstados key={id} />;
      case "calendario_mensual":
        return datos ? (
          <div key={id} className="overflow-x-auto rounded-2xl border border-zinc-200 bg-white shadow-sm">
            <TablaCalendario datos={datos} year={year} month={month} diaHoy={diaHoy} />
          </div>
        ) : null;
      case "resumen_vacaciones":
        return <PanelVacaciones key={id} vacData={vacData} empleadoSel={empleadoSel} />;
      case "listado_trabajadores":
        return (
          <ListadoTrabajadoresVacaciones
            key={id}
            vacData={vacData}
            year={year}
            empleadoSel={empleadoSel}
            onSelectEmpleado={setEmpleadoSel}
          />
        );
      case "solicitudes_pendientes":
        return (
          <SolicitudesPendientes
            key={id}
            vacData={vacData}
            onAprobar={(p) => revisarSolicitud(p, "aprobar")}
            onRechazar={(p) => revisarSolicitud(p, "rechazar")}
            aprobando={aprobando}
          />
        );
      case "disponibilidad_critica":
        return (
          <SeccionDisponibilidad
            key={id}
            desde={dispDesde}
            hasta={dispHasta}
            onDesde={setDispDesde}
            onHasta={setDispHasta}
            disponibilidad={dispData}
            cargando={dispCargando}
          />
        );
      default:
        return null;
    }
  };

  if (!planConfig) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-teal-200 border-t-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="planificacion-page">
      <PageHeader
        icon={CalendarRange}
        title="Planificación"
        subtitle="Vacaciones (naturales o hábiles según trabajador), bajas, ausencias y retrasos"
        sectionKey="planificacion"
        brandGradient={false}
      >
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="gap-1.5 border-0 bg-white/15 text-white hover:bg-white/25"
            onClick={() => setConfigOpen(true)}
            data-testid="planificacion-personalizar-btn"
          >
            <Settings2 className="h-4 w-4" />
            Personalizar
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navegar(-1)}
            data-testid="mes-anterior-btn"
            className="rounded-lg text-white hover:bg-white/20"
          >
            <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
          </Button>
          <span className="font-display w-44 text-center text-lg font-medium text-white" data-testid="mes-actual-label">
            {MESES[month - 1]} {year}
          </span>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navegar(1)}
            data-testid="mes-siguiente-btn"
            className="rounded-lg text-white hover:bg-white/20"
          >
            <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
          </Button>
          <Button
            onClick={exportarPdf}
            data-testid="exportar-planificacion-pdf-btn"
            className="ml-1 gap-1.5 rounded-lg bg-white/20 text-white hover:bg-white/30"
          >
            <FileText className="h-4 w-4" strokeWidth={1.5} />
            PDF
          </Button>
        </div>
      </PageHeader>

      {visibleSet.size === 0 ? (
        <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 p-8 text-center">
          <p className="text-sm text-zinc-600">No tienes ninguna sección visible en planificación.</p>
          <Button type="button" variant="outline" size="sm" className="mt-3" onClick={() => setConfigOpen(true)}>
            Configurar vista
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {seccionesVisibles.map((id) => renderSeccion(id))}
        </div>
      )}

      <PanelConfigDialog
        open={configOpen}
        onClose={() => setConfigOpen(false)}
        configPath="/planificacion/config"
        title="Personalizar planificación"
        description="Elige qué bloques quieres ver y en qué orden. La configuración es personal y solo afecta a tu usuario."
        grupos={GRUPOS_PLANIFICACION}
        testIdPrefix="planificacion"
        onSaved={(data) => {
          setPlanConfig(data);
          setDatos(null);
          setVacData(null);
          setDispData(null);
        }}
      />
    </div>
  );
}
